"use client";

import { useMemo, useState } from "react";
import Link from "next/link";
import { Box, Button, Stack, TextField, Typography } from "@mui/material";
import { DataGrid, type GridColDef } from "@mui/x-data-grid";
import type { StudentRow } from "../../lib/mock-data";
import { SectionCard } from "../ui/SectionCard";
import { StatusChip } from "../ui/StatusChip";

const columns: GridColDef<StudentRow>[] = [
  { field: "name", headerName: "Nama murid", flex: 1.1, minWidth: 118 },
  { field: "family", headerName: "Orang tua", flex: 1, minWidth: 180, sortable: false,
    renderCell: (params) => <Box sx={{ py: 0.75, lineHeight: 1.25 }}>
      <Typography variant="body2">Ayah: {params.row.family?.fatherName || "—"}</Typography>
      <Typography variant="body2">Ibu: {params.row.family?.motherName || "—"}</Typography>
    </Box> },
  {
    field: "className",
    headerName: "Kelas · outlet",
    flex: 1,
    minWidth: 122,
    renderCell: (params) => (
      <Box sx={{ py: 0.75, lineHeight: 1.25 }}>
        {(params.row.classAssignments?.length ? params.row.classAssignments :
          params.row.className ? [{ name: params.row.className, outlet: params.row.outlet, status: undefined }] : []).map((assignment, index) =>
          <Box key={`${assignment.name}-${assignment.outlet}-${index}`} sx={{ mb: 0.5 }}>
            <Typography variant="body2" fontWeight={650}>{assignment.name}</Typography>
            <Typography variant="caption" color="text.secondary">{assignment.outlet}
              {assignment.status ? ` · ${assignment.status === "active" ? "Aktif" : assignment.status === "cuti" ? "Cuti" : "Tidak aktif"}` : ""}</Typography>
          </Box>)}
      </Box>
    ),
  },
  {
    field: "status",
    headerName: "Status",
    width: 92,
    sortable: false,
    renderCell: (params) => <StatusChip label={params.value} tone={params.value === "Aktif" ? "success" : "neutral"} />,
  },
  {
    field: "profile",
    headerName: "Profil",
    width: 80,
    sortable: false,
    renderCell: (params) => <Button component={Link} href={`/students/${params.row.id.split(":")[0]}`} size="small">Ubah</Button>,
  },
];

export function StudentGrid({ rows, preview = false }: { rows: StudentRow[]; preview?: boolean }) {
  const [search, setSearch] = useState("");
  const filteredRows = useMemo(() => {
    const query = search.trim().toLocaleLowerCase("id-ID");
    if (!query) return rows;
    return rows.filter((row) => `${row.name} ${row.family?.fatherName ?? ""} ${row.family?.motherName ?? ""} ${row.classAssignments?.map((item) => `${item.name} ${item.outlet} ${item.status}`).join(" ") ?? `${row.className} ${row.outlet}`} ${row.status}`.toLocaleLowerCase("id-ID").includes(query));
  }, [rows, search]);

  return (
    <Stack spacing={1.5}>
      <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
        <TextField
          label="Cari murid, orang tua, kelas, outlet"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          size="small"
          fullWidth
          inputProps={{ "aria-label": "Cari murid" }}
        />
        <Button component={Link} href="/students/new" variant="contained" sx={{ flexShrink: 0 }}>
          Murid baru
        </Button>
      </Stack>
      <SectionCard sx={{ p: 0, overflow: "hidden" }}>
        <Box sx={{ width: "100%", height: 640, "& .MuiDataGrid-root": { border: 0 }, "& .MuiDataGrid-columnHeaders": { bgcolor: "#fafaf8" }, "& .MuiDataGrid-cell": { borderColor: "divider" } }}>
          <DataGrid
            rows={filteredRows}
            columns={columns}
            initialState={{ pagination: { paginationModel: { pageSize: 50, page: 0 } } }}
            pageSizeOptions={[25, 50, 100]}
            disableColumnMenu
            disableColumnFilter
            disableColumnSelector
            disableRowSelectionOnClick
            getRowHeight={() => "auto"}
            getEstimatedRowHeight={() => 72}
            columnHeaderHeight={44}
            localeText={{ noRowsLabel: "Tidak ada murid", noResultsOverlayLabel: "Murid tidak ditemukan" }}
          />
        </Box>
      </SectionCard>
      <Typography variant="caption" color="text.secondary">{filteredRows.length} baris{preview ? " contoh" : ""} · pencarian pada daftar yang sudah dimuat.</Typography>
    </Stack>
  );
}
